import { useState, useMemo, useCallback, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { SavedLists } from '@/components/SavedLists';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ProfileDetail } from '@/components/ProfileDetail';
import { BlurText } from '@/components/BlurText';
import { usePayment } from '@/context/PaymentContext';
import { 
  Search, 
  MapPin, 
  Building2, 
  Filter,
  ChevronLeft,
  ChevronRight,
  ArrowRight,
  Lock,
  X,
  Zap,
  Info,
  AlertCircle,
  Mail,
  GraduationCap,
  Phone,
  Plus,
  UserPlus,
  Briefcase
} from 'lucide-react';
import { useCRM } from '@/hooks/useCRM';
import { useToast } from '@/hooks/use-toast';
import type { Contact, ContactStage } from '@/types/contacts';
import { curatedInvestors, accessibleInvestors } from '@/data/curatedInvestors';
import type { Investor } from '@/types';
import { SEO, generateLocationSchema, generateIndustrySchema } from '@/components/SEO';
import { SimpleBackdrop } from '@/components/AnimatedBackdrop';
// import { analytics } from '@/services/analytics';

// Score investors by data completeness - EMAIL + PHONE are most important
function scoreInvestor(investor: Investor): number {
  let score = 0;
  
  // PHONE (40 points) - Most important for outreach
  if (investor.phone && investor.phone.match(/\(\d{3}\)\s*\d{3}-\d{4}/)) {
    score += 40;
  } else if (investor.phone && investor.phone.length >= 10) {
    score += 35;
  }
  
  // EMAIL (35 points) - Second most important
  if (investor.contactInfo && investor.contactInfo.includes('@') && !investor.contactInfo.includes('@example.com')) {
    score += 35;
  } else if (investor.contactInfo && investor.contactInfo.includes('@')) {
    score += 20; // Generated email still counts
  }
  
  // Website (10 points)
  if (investor.website && investor.website.length > 10 && !investor.website.includes('google.com')) {
    score += 10;
  }
  
  // LinkedIn (5 points)
  if (investor.linkedin && investor.linkedin.includes('linkedin.com')) {
    score += 5;
  }
  
  // Location quality (5 points)
  if (investor.location && investor.location.includes(',') && investor.location !== 'United States') {
    score += 5;
  }
  
  // Bio/description (3 points)
  if (investor.bio && investor.bio.length > 50) {
    score += 3;
  }
  
  // Portfolio companies (2 points)
  if (investor.portfolioCompanies && investor.portfolioCompanies.length > 0) {
    score += 2;
  }
  
  return score;
}

// Use curated investors only - quality over quantity
const allInvestorsRaw: Investor[] = curatedInvestors;

// Remove duplicates by ID and sort by score
const seen = new Set<string>();
const allInvestors: Investor[] = allInvestorsRaw
  .filter(inv => {
    if (seen.has(inv.id)) return false;
    seen.add(inv.id);
    return true;
  })
  .map(inv => ({ ...inv, dataScore: scoreInvestor(inv) }))
  .sort((a, b) => (b.dataScore || 0) - (a.dataScore || 0));

// Filter options based on actual 5,232 investor data
const LOCATIONS = [
  'All Locations',
  'United States',
  'San Francisco, CA',
  'New York, NY',
  'Menlo Park, CA',
  'Palo Alto, CA',
  'Boston, MA',
  'London, UK',
  'Tel Aviv, Israel',
  'Singapore',
  'Bangalore, India',
  'Chicago, IL',
  'Austin, TX',
  'Seattle, WA',
  'Santa Monica, CA',
  'Sydney, Australia',
  'Mountain View, CA',
  'Berlin, Germany',
  'Washington, DC',
  'Cambridge, MA',
  'Sunnyvale, CA',
  'Princeton, NJ',
  'Houston, TX',
  'Columbus, OH',
  'Baltimore, MD',
  'Stockholm, Sweden',
  'Minneapolis, MN',
  'Dallas, TX',
  'Bellevue, WA',
  'Los Angeles, CA',
  'Santa Clara, CA',
  'Jerusalem, Israel',
  'Boulder, CO',
  'Amsterdam, NL',
  'Irvine, CA',
  'Atlanta, GA',
  'Hong Kong',
  'Oakland, CA',
  'Kirkland, WA',
  'Jackson, WY',
  'Tokyo, Japan',
  'Shenzhen, China',
  'Hangzhou, China',
  'St. Cloud, MN',
  'Pasadena, CA',
  'Melbourne, Australia',
  'San Mateo, CA',
  'Victoria, BC',
  'Vienna, Austria',
  'New Haven, CT',
  'Berkeley, CA',
  'Toronto, ON',
  'Pleasanton, CA'
];

const INDUSTRIES = [
  'All Industries',
  'Technology',
  'Enterprise',
  'Consumer',
  'Healthcare',
  'Fintech',
  'AI',
  'Software',
  'Crypto',
  'Biotech',
  'SaaS',
  'DeFi',
  'Web3',
  'Marketplace',
  'Climate',
  'Robotics',
  'Security',
  'B2B',
  'Hardware',
  'Energy',
  'Deep Tech',
  'Cloud',
  'Media',
  'Health',
  'Clean Tech'
];

const STAGES = [
  'All Stages',
  'Growth',
  'Growth - Buyout',
  'Pre-seed',
  'Pre-seed - Seed',
  'Pre-seed - Series A',
  'Seed',
  'Seed - Growth',
  'Seed - Series A',
  'Seed - Series B',
  'Seed - Series C',
  'Seed - Series D',
  'Series A - B',
  'Series A - Growth',
  'Series B - Growth',
  'Series B - IPO',
  'Series C - IPO'
];

// Note: Series D+ and Late Stage removed - no matching data (verified 2024)

const INVESTOR_TYPES = [
  'All Types',
  'Angel',
  'Warm Intro',
  'Micro-VC',
  'VC'
];

const SCHOOLS = [
  'All Schools',
  'Stanford University',
  'Johns Hopkins University',
  'Georgia Tech',
  'Yale University',
  'University of Michigan',
  'Princeton University',
  'MIT',
  'Carnegie Mellon University',
  'Dartmouth College',
  'University of Pennsylvania',
  'UC Berkeley',
  'University of Texas at Austin',
  'University of Toronto',
  'University of Chicago',
  'Duke University',
  'Northwestern University',
  'University of Georgia',
  'University of Montreal'
];

const CONTACT_METHODS = [
  'Any Contact',
  'Has Phone',
  'Has LinkedIn',
  'Has Website'
];

// Filter counts verified against actual data (215 investors with school affiliations)

export function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isSubscribed } = usePayment();
  const { isAdmin, isApproved, pendingApproval, isAuthenticated } = useAuth();
  const { createContact, contacts } = useCRM();
  const { toast } = useToast();
  const [savedListsOpen, setSavedListsOpen] = useState(false);
  // Check if investor is already in CRM by name
  const isInCRM = useCallback((investor: Investor) => {
    return contacts.some(c => c.name.toLowerCase() === investor.name.toLowerCase());
  }, [contacts]);

  // State - initialize from URL
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('q') || '');
  const [location, setLocation] = useState(() => searchParams.get('location') || 'All Locations');
  const [industry, setIndustry] = useState(() => searchParams.get('industry') || 'All Industries');
  const [stage, setStage] = useState(() => searchParams.get('stage') || 'All Stages');
  const [investorType, setInvestorType] = useState(() => searchParams.get('type') || 'All Types');
  const [school, setSchool] = useState(() => searchParams.get('school') || 'All Schools');
  const [contactMethod, setContactMethod] = useState(() => searchParams.get('contact') || 'Any Contact');
  
  // Load founder profile from onboarding
  const [founderProfile, setFounderProfile] = useState<any>(null);
  
  useEffect(() => {
    const profile = localStorage.getItem('backr_founder_profile');
    if (profile) {
      setFounderProfile(JSON.parse(profile));
    }
  }, []);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedInvestor, setSelectedInvestor] = useState<Investor | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [clickPosition, setClickPosition] = useState<{ x: number; y: number } | null>(null);
  
  const RESULTS_PER_PAGE = 9;

  // Sync URL params to state on mount and when URL changes
  useEffect(() => {
    setSearchQuery(searchParams.get('q') || '');
    setLocation(searchParams.get('location') || 'All Locations');
    setIndustry(searchParams.get('industry') || 'All Industries');
    setStage(searchParams.get('stage') || 'All Stages');
    setInvestorType(searchParams.get('type') || 'All Types');
    setSchool(searchParams.get('school') || 'All Schools');
    setContactMethod(searchParams.get('contact') || 'Any Contact');
  }, [searchParams]);

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchQuery) params.set('q', searchQuery);
    if (location !== 'All Locations') params.set('location', location);
    if (industry !== 'All Industries') params.set('industry', industry);
    if (stage !== 'All Stages') params.set('stage', stage);
    if (investorType !== 'All Types') params.set('type', investorType);
    if (school !== 'All Schools') params.set('school', school);
    if (contactMethod !== 'Any Contact') params.set('contact', contactMethod);
    setSearchParams(params, { replace: true });
    setCurrentPage(1);
  }, [searchQuery, location, industry, stage, investorType, school, contactMethod, setSearchParams]);

  // Track search events - disabled
  // useEffect(() => {
  //   const timeout = setTimeout(() => {
  //     if (searchQuery || hasActiveFilters) {
  //       try {
  //         analytics.trackSearch(searchQuery, {
  //           location: location !== 'All Locations' ? location : '',
  //           industry: industry !== 'All Industries' ? industry : '',
  //           stage: stage !== 'All Stages' ? stage : '',
  //           type: investorType !== 'All Types' ? investorType : '',
  //           school: school !== 'All Schools' ? school : '',
  //         }, totalResults);
  //       } catch {}
  //     }
  //   }, 1000);
  //   return () => clearTimeout(timeout);
  // }, [searchQuery, location, industry, stage, investorType, school, totalResults]);

  // Smart matching: Score investors based on founder profile + filters
  const { 
    exactMatches, 
    partialMatches,
    suggestions 
  } = useMemo(() => {
    // Calculate match score for each investor
    const scoredInvestors = allInvestors.map((investor: Investor) => {
      let score = 0;
      let matchReasons: string[] = [];
      
      // Search query match (highest priority)
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase().trim();
        const matchesSearch = 
          investor.name.toLowerCase().includes(query) ||
          investor.firm.toLowerCase().includes(query) ||
          investor.location.toLowerCase().includes(query) ||
          investor.industries.some(i => i.toLowerCase().includes(query));
        if (!matchesSearch) return { investor, score: -1, matchReasons }; // Exclude if doesn't match search
        score += 10;
      }
      
      // Founder profile matching (if available)
      if (founderProfile) {
        // Match industries from founder profile
        if (founderProfile.industry && founderProfile.industry.length > 0) {
          const matchingIndustries = investor.industries.filter(invInd => 
            founderProfile.industry.some((profInd: string) => 
              invInd.toLowerCase().includes(profInd.toLowerCase()) ||
              profInd.toLowerCase().includes(invInd.toLowerCase())
            )
          );
          score += matchingIndustries.length * 5;
          if (matchingIndustries.length > 0) matchReasons.push(`Industry match: ${matchingIndustries[0]}`);
        }
        
        // Match stage
        if (founderProfile.stage && investor.stage.toLowerCase().includes(founderProfile.stage.toLowerCase())) {
          score += 3;
          matchReasons.push(`Stage: ${investor.stage}`);
        }
        
        // Match location
        if (founderProfile.location && investor.location.toLowerCase().includes(founderProfile.location.toLowerCase())) {
          score += 4;
          matchReasons.push(`Location: ${investor.location}`);
        }
      }
      
      // Filter matching (additional scoring)
      if (location !== 'All Locations') {
        const selectedState = location.split(' ').pop();
        const locationLower = investor.location.toLowerCase();
        const matchesLocation = 
          investor.location === location ||
          investor.location === selectedState ||
          (selectedState && investor.location.endsWith(' ' + selectedState)) ||
          (selectedState && investor.state === selectedState) ||
          locationLower.includes(location.toLowerCase());
        if (matchesLocation) {
          score += 3;
          matchReasons.push(`Location: ${location}`);
        }
      }
      
      if (industry !== 'All Industries') {
        if (investor.industries.some(inv => inv.toLowerCase().includes(industry.toLowerCase()))) {
          score += 3;
          matchReasons.push(`Industry: ${industry}`);
        }
      }
      
      if (investorType !== 'All Types' && investor.investorType === investorType) {
        score += 2;
        matchReasons.push(`Type: ${investorType}`);
      }
      
      if (school !== 'All Schools' && investor.school === school) {
        score += 5; // High score for school match
        matchReasons.push(`Alumni: ${school}`);
      }
      
      // Contact method filter
      if (contactMethod !== 'Any Contact') {
        if (contactMethod === 'Has Phone' && (!investor.phone || investor.phone === '')) {
          return { investor, score: -1, matchReasons };
        }
        if (contactMethod === 'Has LinkedIn' && (!investor.linkedin || investor.linkedin === '')) {
          return { investor, score: -1, matchReasons };
        }
        if (contactMethod === 'Has Website' && (!investor.website || investor.website === '')) {
          return { investor, score: -1, matchReasons };
        }
      }
      
      return { investor, score, matchReasons };
    }).filter(item => item.score >= 0); // Remove excluded investors
    
    // Sort by score (highest first)
    scoredInvestors.sort((a, b) => b.score - a.score);
    
    // Split into best matches (score > 5) and others
    const bestMatches = scoredInvestors.filter(item => item.score >= 5).map(item => item.investor);
    const otherMatches = scoredInvestors.filter(item => item.score < 5).map(item => item.investor);
    
    // If no active filters and no founder profile, show all as "best matches"
    const hasActiveFilters = 
      location !== 'All Locations' || 
      industry !== 'All Industries' || 
      stage !== 'All Stages' ||
      investorType !== 'All Types' ||
      school !== 'All Schools' ||
      searchQuery.trim() !== '';
      
    if (!hasActiveFilters && !founderProfile) {
      return { 
        exactMatches: scoredInvestors.map(item => item.investor), 
        partialMatches: [],
        suggestions: [] 
      };
    }
    
    return { 
      exactMatches: bestMatches.length > 0 ? bestMatches : scoredInvestors.map(item => item.investor), 
      partialMatches: bestMatches.length > 0 ? otherMatches : [],
      suggestions: [] 
    };
  }, [searchQuery, location, industry, stage, investorType, school, contactMethod, founderProfile]);

  const totalResults = exactMatches.length + partialMatches.length;
  const allResults = [...exactMatches, ...partialMatches];
  const totalPages = Math.ceil(totalResults / RESULTS_PER_PAGE);
  
  // Smart pagination: ensure partial matches fill at least 3 spots if available
  const getPaginatedResults = (page: number) => {
    const startIdx = (page - 1) * RESULTS_PER_PAGE;
    const endIdx = page * RESULTS_PER_PAGE;
    
    // Get exact matches for this page
    const exactStart = startIdx;
    const exactEnd = Math.min(endIdx, exactMatches.length);
    const pageExact = exactMatches.slice(exactStart, exactEnd);
    
    // Fill remaining spots with partial matches
    const remainingSlots = RESULTS_PER_PAGE - pageExact.length;
    const partialStart = Math.max(0, startIdx - exactMatches.length);
    const partialEnd = partialStart + remainingSlots;
    const pagePartial = partialMatches.slice(partialStart, partialEnd);
    
    // If we have fewer than 3 partial matches on this page and more exist, add them
    if (pagePartial.length > 0 && pagePartial.length < 3 && partialMatches.length > partialEnd) {
      const additionalPartial = partialMatches.slice(partialEnd, partialEnd + (3 - pagePartial.length));
      // Adjust exact matches to make room
      const adjustedExact = pageExact.slice(0, RESULTS_PER_PAGE - (pagePartial.length + additionalPartial.length));
      return [...adjustedExact, ...pagePartial, ...additionalPartial];
    }
    
    return [...pageExact, ...pagePartial];
  };
  
  const paginatedResults = getPaginatedResults(currentPage);

  const hasActiveFilters = 
    location !== 'All Locations' || 
    industry !== 'All Industries' || 
    stage !== 'All Stages' || 
    investorType !== 'All Types' ||
    school !== 'All Schools' ||
    contactMethod !== 'Any Contact' ||
    searchQuery.trim() !== '';

  const hasExpandedResults = partialMatches.length > 0;

  const handleInvestorClick = useCallback((investor: Investor, event: React.MouseEvent) => {
    // try { analytics.trackInvestorView(investor.id, investor.name); } catch {}
    setClickPosition({ x: event.clientX, y: event.clientY });
    setSelectedInvestor(investor);
    setDetailOpen(true);
  }, []);

  const handleAddToCRM = useCallback(async (investor: Investor) => {
    if (isInCRM(investor)) {
      toast({
        title: 'Already in your network',
        description: `${investor.name} is already saved to your network`,
      });
      return;
    }
    
    try {
      const contactData = {
        name: investor.name,
        email: investor.contactInfo || '',
        phone: investor.phone || '',
        company: investor.firm,
        title: investor.role || investor.investorType,
        stage: 'new' as ContactStage,
        source: 'RaiseBeacon Search',
        tags: [...investor.industries, investor.investorType].filter(Boolean),
        value: 0,
        location: investor.location,
        website: investor.website || '',
        linkedin: investor.linkedin || '',
        notes: '', // Notes are for user input only
      };
      
      await createContact(contactData);
      // Contact added to CRM
    } catch (error) {
      // Error handling in useCRM
    }
  }, [createContact, toast, isInCRM]);

  const clearFilters = () => {
    setSearchQuery('');
    setLocation('All Locations');
    setIndustry('All Industries');
    setStage('All Stages');
    setInvestorType('All Types');
    setSchool('All Schools');
    setContactMethod('Any Contact');
  };

  // Generate smart suggestion message
  const getSuggestionMessage = () => {
    if (suggestions.length === 0) return null;
    
    const topSuggestion = suggestions[0];
    const filterLabels: Record<string, string> = {
      location: 'location',
      industry: 'industry',
      type: 'investor type',
      school: 'school alumni',
      'contact method': 'contact method'
    };
    
    return {
      text: `Removing "${filterLabels[topSuggestion.filter]}" would show ${topSuggestion.count} more results`,
      action: () => {
        if (topSuggestion.filter === 'location') setLocation('All Locations');
        if (topSuggestion.filter === 'industry') setIndustry('All Industries');
        if (topSuggestion.filter === 'type') setInvestorType('All Types');
        if (topSuggestion.filter === 'school') setSchool('All Schools');
        if (topSuggestion.filter === 'contact method') setContactMethod('Any Contact');
      }
    };
  };

  const suggestion = getSuggestionMessage();

  // Generate dynamic SEO based on filters
  const getSEOTitle = () => {
    const parts: string[] = [];
    if (location !== 'All Locations') parts.push(location);
    if (industry !== 'All Industries') parts.push(industry);
    if (investorType !== 'All Types') parts.push(investorType);
    if (stage !== 'All Stages') parts.push(stage);
    if (school !== 'All Schools') parts.push(`${school} Alumni`);
    if (searchQuery) parts.push(`"${searchQuery}"`);
    
    if (parts.length === 0) return 'Search Investors - RaiseBeacon';
    return `${parts.join(' ')} Investors - RaiseBeacon`;
  };

  const getSEODescription = () => {
    let desc = `Find ${totalResults}+ verified investors`;
    if (location !== 'All Locations') desc += ` in ${location}`;
    if (industry !== 'All Industries') desc += ` specializing in ${industry}`;
    if (investorType !== 'All Types') desc += ` - ${investorType}`;
    if (stage !== 'All Stages') desc += ` at ${stage} stage`;
    if (school !== 'All Schools') desc += ` from ${school}`;
    desc += '. Connect with the right investors for your startup.';
    return desc;
  };

  const getSEOSchema = () => {
    if (location !== 'All Locations' && industry === 'All Industries' && investorType === 'All Types') {
      return generateLocationSchema(location, totalResults);
    }
    if (industry !== 'All Industries' && location === 'All Locations' && investorType === 'All Types') {
      return generateIndustrySchema(industry, totalResults);
    }
    return undefined;
  };

  return (
    <>
      <SEO
        title={getSEOTitle()}
        description={getSEODescription()}
        canonicalUrl={`https://backr.us/#/search${location !== 'All Locations' ? `?location=${encodeURIComponent(location)}` : ''}${industry !== 'All Industries' ? `${location !== 'All Locations' ? '&' : '?'}industry=${encodeURIComponent(industry)}` : ''}`}
        schema={getSEOSchema()}
      />
        <SimpleBackdrop />
        <div className="min-h-screen relative">
        
      {/* Search Section */}
      <div className="bg-background border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-3xl font-semibold text-foreground mb-2">Your Matches</h1>
          <p className="text-muted-foreground mb-8">Investors curated based on your profile and preferences</p>

          {/* Search Bar */}
          <div className="relative mb-6" data-tutorial="search">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              type="text"
              placeholder="Search by name, firm, or keyword..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 pr-4 py-6 text-lg bg-muted border-0 rounded-2xl focus:ring-2 focus:ring-[#2B7FFF]/20 focus:bg-background transition-all"
            />
          </div>

          {/* Filters */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-tutorial="filters">
            <FilterSelect
              value={location}
              onValueChange={setLocation}
              placeholder="Location"
              options={LOCATIONS}
              icon={MapPin}
            />
            <FilterSelect
              value={industry}
              onValueChange={setIndustry}
              placeholder="Industry"
              options={INDUSTRIES}
              icon={Building2}
            />
            <FilterSelect
              value={stage}
              onValueChange={setStage}
              placeholder="Stage"
              options={STAGES}
              icon={Filter}
            />
            <FilterSelect
              value={investorType}
              onValueChange={setInvestorType}
              placeholder="Type"
              options={INVESTOR_TYPES}
              icon={Building2}
            />
            <FilterSelect
              value={school}
              onValueChange={setSchool}
              placeholder="School Alumni"
              options={SCHOOLS}
              icon={GraduationCap}
            />
            <FilterSelect
              value={contactMethod}
              onValueChange={setContactMethod}
              placeholder="Contact Method"
              options={CONTACT_METHODS}
              icon={Phone}
            />
          </div>

          {/* Active Filters - Clean blue style */}
          {hasActiveFilters && (
            <div className="flex items-center gap-2 mt-4 flex-wrap">
              <span className="text-sm text-muted-foreground">Active filters:</span>
              {location !== 'All Locations' && (
                <FilterTag label={location} onRemove={() => setLocation('All Locations')} />
              )}
              {industry !== 'All Industries' && (
                <FilterTag label={industry} onRemove={() => setIndustry('All Industries')} />
              )}
              {stage !== 'All Stages' && (
                <FilterTag label={stage} onRemove={() => setStage('All Stages')} />
              )}
              {investorType !== 'All Types' && (
                <FilterTag label={investorType} onRemove={() => setInvestorType('All Types')} />
              )}
              {school !== 'All Schools' && (
                <FilterTag label={school} onRemove={() => setSchool('All Schools')} />
              )}
              {contactMethod !== 'Any Contact' && (
                <FilterTag label={contactMethod} onRemove={() => setContactMethod('Any Contact')} />
              )}
              {searchQuery && (
                <FilterTag label={`"${searchQuery}"`} onRemove={() => setSearchQuery('')} />
              )}
              <button 
                onClick={clearFilters}
                className="text-sm text-[#2B7FFF] hover:underline ml-2"
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Results */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Smart Suggestion Banner */}
        {suggestion && exactMatches.length < 10 && (
          <div className="mb-6 bg-gradient-to-r from-[#2B7FFF]/5 to-[#1A5FCC]/5 border border-[#2B7FFF]/20 rounded-2xl p-4 flex items-start gap-3">
            <Zap className="w-5 h-5 text-[#2B7FFF] flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-slate-700 text-sm">
                {suggestion.text}
              </p>
              <button 
                onClick={suggestion.action}
                className="text-sm text-[#2B7FFF] font-medium hover:underline mt-1"
              >
                Try it →
              </button>
            </div>
          </div>
        )}

        {/* Expansion Info Banner */}
        {hasExpandedResults && (
          <div className="mb-6 bg-muted border border-border rounded-2xl p-4 flex items-start gap-3">
            <Info className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-muted-foreground text-sm">
                Found <strong>{exactMatches.length}</strong> exact matches. 
                Also showing <strong>{partialMatches.length}</strong> similar investors.
              </p>
            </div>
          </div>
        )}

        {/* Results Count */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <p className="text-muted-foreground">
              <span className="font-semibold text-foreground">{totalResults}</span> matches for you
            </p>
            {hasExpandedResults && (
              <span className="text-sm text-slate-400">
                ({exactMatches.length} best, {partialMatches.length} similar)
              </span>
            )}
          </div>
          {!isSubscribed && (
            <p className="text-sm text-muted-foreground flex items-center gap-1">
              <Lock className="w-3 h-3" />
              Contact info locked until subscription
            </p>
          )}
        </div>

        {/* Results Sections */}
        {totalResults > 0 ? (
          <>
            {/* Exact Matches Section */}
            {exactMatches.length > 0 && (
              <div className="mb-8">
                {hasExpandedResults && (
                  <h3 className="text-sm font-medium text-[#2B7FFF] uppercase tracking-wide mb-4">
                    Best Matches for You
                  </h3>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-tutorial="investor-card">
                  {paginatedResults
                    .filter(inv => exactMatches.includes(inv))
                    .map((investor, idx) => (
                      <div key={investor.id} className={idx === 0 ? 'tutorial-highlight' : ''}>
                        <ProfileCard
                          investor={investor}
                          isSubscribed={isSubscribed}
                          isApproved={isApproved}
                          isAdmin={isAdmin}
                          isAuthenticated={isAuthenticated}
                          onClick={(e) => handleInvestorClick(investor, e)}
                          onAddToCRM={handleAddToCRM}
                          isAddedToCRM={isInCRM(investor)}
                          matchType="exact"
                        />
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Partial Matches Section */}
            {partialMatches.length > 0 && paginatedResults.some(inv => partialMatches.includes(inv)) && (
              <div>
                <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-4">
                  You Might Also Like
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {paginatedResults
                    .filter(inv => partialMatches.includes(inv))
                    .map((investor) => (
                      <ProfileCard
                        key={investor.id}
                        investor={investor}
                        isSubscribed={isSubscribed}
                        isApproved={isApproved}
                        isAdmin={isAdmin}
                        isAuthenticated={isAuthenticated}
                        onClick={(e) => handleInvestorClick(investor, e)}
                        onAddToCRM={handleAddToCRM}
                        isAddedToCRM={isInCRM(investor)}
                        matchType="partial"
                      />
                    ))}
                </div>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-12">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="rounded-full border-border"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                
                <span className="text-sm text-muted-foreground px-4">
                  Page {currentPage} of {totalPages}
                </span>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="rounded-full border-border"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-20">
            <div className="w-20 h-20 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Search className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-xl font-semibold text-foreground mb-2">No investors found</h3>
            <p className="text-muted-foreground max-w-md mx-auto mb-6">
              We couldn't find any investors matching your criteria. Try adjusting your filters.
            </p>
            {suggestion && (
              <Button
                onClick={suggestion.action}
                variant="outline"
                className="rounded-full mb-4"
              >
                {suggestion.text}
              </Button>
            )}
            <div>
              <Button
                onClick={clearFilters}
                variant="outline"
                className="rounded-full"
              >
                Clear all filters
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Profile Detail Modal */}
      <ProfileDetail
        investor={selectedInvestor}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        originPosition={clickPosition}
        onAddToCRM={handleAddToCRM}
        isAddedToCRM={selectedInvestor ? isInCRM(selectedInvestor) : false}
      />

      {/* Saved Lists */}
      <SavedLists 
        open={savedListsOpen} 
        onOpenChange={setSavedListsOpen}
        currentInvestorIds={allResults.map(i => i.id)}
      />

      {/* Pending Approval Banner */}
      {isAuthenticated && pendingApproval && (
        <div className="fixed bottom-0 left-0 right-0 bg-amber-500 text-white p-4 z-50">
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-3">
            <AlertCircle className="w-5 h-5" />
            <span className="font-medium">Your account is pending approval from an administrator</span>
          </div>
        </div>
      )}

      </div>
    </>
  );
}

// Filter Select Component
function FilterSelect({ 
  value, 
  onValueChange, 
  placeholder, 
  options,
  icon: Icon 
}: { 
  value: string; 
  onValueChange: (value: string) => void; 
  placeholder: string; 
  options: string[];
  icon: React.ElementType;
}) {
  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger className="bg-muted border-0 rounded-xl h-12 focus:ring-2 focus:ring-[#2B7FFF]/20">
        <Icon className="w-4 h-4 text-slate-400 mr-2" />
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="rounded-xl max-h-[300px]">
        {options.map((option) => (
          <SelectItem key={option} value={option} className="rounded-lg cursor-pointer">
            {option}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// Clean Filter Tag - no warning colors
function FilterTag({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span className="inline-flex items-center gap-1 bg-[#2B7FFF]/10 text-[#2B7FFF] rounded-full px-3 py-1 text-sm">
      {label}
      <button 
        onClick={onRemove} 
        className="hover:bg-[#2B7FFF]/20 rounded-full p-0.5 transition-colors"
        type="button"
      >
        <X className="w-3 h-3" />
      </button>
    </span>
  );
}

// Profile Card with subtle match type indicator
function ProfileCard({ 
  investor, 
  isSubscribed, 
  isApproved,
  isAdmin,
  isAuthenticated,
  onClick,
  onOutreachClick,
  onAddToCRM,
  isAddedToCRM,
  matchType
}: { 
  investor: Investor; 
  isSubscribed: boolean; 
  isApproved: boolean;
  isAdmin: boolean;
  isAuthenticated: boolean;
  onClick: (e: React.MouseEvent) => void;
  onAddToCRM?: (investor: Investor) => void;
  isAddedToCRM?: boolean;
  matchType: 'exact' | 'partial';
}) {
  const canViewDetails = (isSubscribed && isApproved) || isAdmin;
  
  return (
    <div
      onClick={onClick}
      className={`group bg-card rounded-3xl p-6 shadow-sm hover:shadow-xl border transition-all duration-300 cursor-pointer relative overflow-hidden ${
        matchType === 'exact'
          ? 'border-border hover:border-[#2B7FFF]/20'
          : 'border-border hover:border-border/80'
      } ${!canViewDetails ? 'bg-slate-900/50' : ''}`}
    >

      {/* Header */}
      <div className="flex items-start justify-between mb-4 relative z-10">
        <div className="flex-1 min-w-0 overflow-hidden">
          <div className="flex items-center gap-2">
            {!canViewDetails ? (
              <h3 className="font-semibold text-lg text-foreground truncate" title={investor.name}>
                <BlurText>{investor.name}</BlurText>
              </h3>
            ) : (
              <h3 className="font-semibold text-lg text-foreground truncate" title={investor.name}>
                {investor.name}
              </h3>
            )}
            {matchType === 'partial' && (
              <span className="text-xs text-slate-400 whitespace-nowrap flex-shrink-0">similar</span>
            )}
          </div>
          {!canViewDetails ? (
            <p className="text-sm text-muted-foreground truncate" title={investor.firm === 'Google Maps' ? '' : investor.firm}>
              <BlurText>{investor.firm === 'Google Maps' ? '\u00A0' : investor.firm}</BlurText>
            </p>
          ) : (
            <p className="text-sm text-muted-foreground truncate" title={investor.firm === 'Google Maps' ? '' : investor.firm}>
              {investor.firm === 'Google Maps' ? '\u00A0' : investor.firm}
            </p>
          )}
        </div>
        <Badge 
          variant="secondary" 
          className="bg-muted text-muted-foreground rounded-full text-xs font-medium flex-shrink-0 ml-2"
        >
          {investor.investorType}
        </Badge>
      </div>

      {/* Location, Check Size & Industries */}
      <div className="space-y-2 mb-4 relative z-10">
        {!canViewDetails ? (
          <div className="flex items-center gap-2 text-sm">
            <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0" />
            <BlurText className="truncate text-muted-foreground">{investor.location}</BlurText>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-sm">
            <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0" />
            <span className="truncate text-muted-foreground">{investor.location}</span>
          </div>
        )}
        
        {investor.checkSize && (
          !canViewDetails ? (
            <div className="flex items-center gap-2 text-sm overflow-hidden rounded-sm" style={{ filter: 'blur(6px)', opacity: 0.6 }}>
              <span className="text-xs bg-green-100/50 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full px-2 py-0.5 font-medium select-none">
                {investor.checkSize}
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-xs bg-green-100/50 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full px-2 py-0.5 font-medium">
                {investor.checkSize}
              </span>
            </div>
          )
        )}
        
        <div className="flex flex-wrap gap-1">
          {investor.industries.slice(0, 3).map((industry) => (
            <span 
              key={industry}
              className="text-xs bg-muted text-muted-foreground rounded-full px-2 py-1"
            >
              {industry}
            </span>
          ))}
          {investor.industries.length > 3 && (
            <span className="text-xs text-slate-400 px-1">
              +{investor.industries.length - 3}
            </span>
          )}
        </div>
        
        {/* School Tag */}
        {investor.school && (
          <div className="flex items-center gap-1 mt-2">
            <GraduationCap className="w-3 h-3 text-blue-500 flex-shrink-0" />
            <span className="text-xs bg-blue-100/50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full px-2 py-0.5 font-medium">
              {investor.school}
            </span>
          </div>
        )}
      </div>

      {/* Stage & CTA */}
      <div className="flex items-center justify-between pt-4 border-t border-border relative z-10">
        {!canViewDetails ? (
          <BlurText className="text-sm text-muted-foreground">{investor.stage}</BlurText>
        ) : (
          <span className="text-sm text-muted-foreground">{investor.stage}</span>
        )}
        
        <div className="flex items-center gap-2">
          {!isAuthenticated ? (
            <span className="text-sm text-muted-foreground flex items-center gap-1">
              <Lock className="w-4 h-4" />
              Login to view
            </span>
          ) : !isApproved ? (
            <span className="text-sm text-amber-600 flex items-center gap-1">
              <Lock className="w-4 h-4" />
              Pending approval
            </span>
          ) : !isSubscribed ? (
            <span className="text-sm text-[#2B7FFF] flex items-center gap-1">
              <Lock className="w-4 h-4" />
              Subscribe to view
            </span>
          ) : (
            <>
              {onAddToCRM && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onAddToCRM(investor);
                  }}
                  disabled={isAddedToCRM}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    isAddedToCRM
                      ? 'bg-green-100 text-green-700 cursor-default'
                      : 'bg-[#2B7FFF]/10 text-[#2B7FFF] hover:bg-[#2B7FFF]/20'
                  }`}
                  title={isAddedToCRM ? 'Already in your network' : 'Save to network'}
                >
                  <Briefcase className="w-3.5 h-3.5" />
                  {isAddedToCRM ? 'Added' : 'Save to network'}
                </button>
              )}
              <span className="text-sm font-medium text-[#2B7FFF] flex items-center gap-1">
                View profile
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

